import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '@/contexts/AuthContext';
import { superadminAPI } from '@/lib/api';
import { formatCellValue } from '@/lib/utils';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Building2, ArrowLeft, BarChart3, Coins, Calendar, Settings, Users, FileText,
  FileQuestion, BookOpen, MessageSquare, Trophy, Link2,
  Zap, Tags, RefreshCw, Sparkles, CheckCircle, XCircle, PowerOff, Power,
  Trash2, Save, Loader2, Shield, Clock, TrendingUp, DollarSign, AlertTriangle,
  FileUp, Plug, Crown, Download, Mail, CreditCard, Receipt, ExternalLink,
  Cloud, Percent, Bell,
} from 'lucide-react';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { PageShell } from '@/components/PageShell';
import { PageSkeleton } from '@/components/PageSkeleton';
import { ViewModeToggle } from '@/components/ViewModeToggle';
import { DataTablePagination, DataTableSortHeader, DataTableToolbar } from '@/components/DataTableControls';
import { UserAvatar } from '@/components/UserAvatar';
import { TenantIcon } from '@/components/TenantIcon';
import ChatThreadsPage from '@/pages/ChatThreadsPage';

const MODEL_OPTIONS = {
  fast: [
    { value: 'claude-sonnet-4-6', label: 'Eva Smart' },
    { value: 'claude-haiku-4-5-20251001', label: 'Eva Fast' },
  ],
  thinking: [
    { value: 'claude-opus-4-6', label: 'Eva Deep' },
    { value: 'claude-sonnet-4-6', label: 'Eva Smart' },
  ],
};

// Formats supported by the ingestion and reading pipeline.
const FILE_TYPES = [
  { ext: 'pdf', label: 'PDF', desc: 'Portable Document Format' },
  { ext: 'docx', label: 'Word', desc: 'Word text and unmerged tables; no images or complex layouts yet' },
  { ext: 'xlsx', label: 'Excel', desc: 'Values-only Excel workbooks' },
];

const BRANDFETCH_INDUSTRY_OPTIONS = [
  'Arts and Entertainment',
  'Business and Consumer Services',
  'Computers Electronics and Technology',
  'E-Commerce and Shopping',
  'Finance',
  'Food and Drink',
  'Health',
  'Jobs and Career',
  'Lifestyle',
  'Luxury',
  'Music',
  'News and Media',
  'Programming and Developer Software',
  'Science and Education',
  'Travel and tourism',
  'Vehicles',
];

const BRANDFETCH_COMPANY_KIND_OPTIONS = [
  'PRIVATELY_HELD',
  'PUBLIC_COMPANY',
  'PUBLICLY_TRADED',
  'EDUCATIONAL',
  'GOVERNMENT',
  'NON_PROFIT',
];

const LIMIT_SOURCE_STYLES = {
  tenant_override: 'border-primary/30 bg-primary/10 text-primary',
  trial_override: 'border-warning/30 bg-warning/10 text-warning',
  plan_default: 'border-border bg-muted text-muted-foreground',
  calculated: 'border-border bg-muted text-muted-foreground',
  not_configured: 'border-destructive/30 bg-destructive/10 text-destructive',
};

const TRIAL_SUBSCRIPTION_STATUSES = new Set(['trial', 'trial_expired']);
const CALCULATED_LIMIT_KEYS = new Set([
  'conversation_monthly_token_limit',
  'monthly_token_limit',
]);

const LIMIT_ICON_MAP = {
  employee_limit: Building2,
  content_team_limit: Users,
  document_limit: FileText,
  content_gap_limit: FileQuestion,
  topics_limit: Tags,
  integrations_limit: Plug,
  monthly_export_limit: Download,
  scheduled_reports_limit: Mail,
  notification_limit: Bell,
  monthly_token_limit: Zap,
  conversation_monthly_token_limit: Zap,
  kb_monthly_token_limit: FileText,
  monthly_cost_limit_usd: DollarSign,
  tokens_per_seat_month: Coins,
  user_daily_capacity_pct: Clock,
  user_weekly_capacity_pct: Calendar,
  user_monthly_capacity_pct: TrendingUp,
  max_chunks_per_document: FileUp,
};

const LIMIT_SECTIONS = [
  {
    key: 'knowledge-base',
    label: 'Knowledge Base',
    icon: BookOpen,
    fields: ['document_limit', 'max_chunks_per_document', 'topics_limit', 'content_gap_limit', 'kb_monthly_token_limit'],
  },
  {
    key: 'people',
    label: 'People',
    icon: Users,
    fields: ['employee_limit', 'content_team_limit'],
  },
  {
    key: 'conversations',
    label: 'Conversations',
    icon: MessageSquare,
    fields: [
      'user_daily_capacity_pct',
      'user_weekly_capacity_pct',
      'user_monthly_capacity_pct',
      'tokens_per_seat_month',
      'conversation_monthly_token_limit',
      'monthly_token_limit',
      'monthly_cost_limit_usd',
    ],
  },
  {
    key: 'engagement',
    label: 'Engagement',
    icon: Trophy,
    fields: ['monthly_export_limit', 'scheduled_reports_limit', 'notification_limit'],
  },
  {
    key: 'connectors',
    label: 'Connectors',
    icon: Link2,
    fields: ['integrations_limit'],
  },
];

const SectionLabel = ({ section, compact = false }) => {
  const Icon = section.icon || Settings;
  return (
    <span className={`inline-flex items-center gap-2 ${compact ? '' : 'font-medium'}`}>
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span>{section.label}</span>
    </span>
  );
};

const groupTenantLimitsBySection = (limits = []) => {
  const limitByKey = new Map(limits.map((limit) => [limit.key, limit]));
  const usedKeys = new Set();
  const grouped = LIMIT_SECTIONS.map((section) => {
    const sectionLimits = section.fields
      .map((key) => {
        const limit = limitByKey.get(key);
        if (limit) usedKeys.add(key);
        return limit;
      })
      .filter(Boolean);
    return { ...section, limits: sectionLimits };
  }).filter((section) => section.limits.length);

  const uncategorized = limits.filter((limit) => !usedKeys.has(limit.key));
  if (uncategorized.length) {
    grouped.push({ key: 'other', label: 'Other', limits: uncategorized });
  }
  return grouped;
};

const buildLimitValueMap = (limitDetails = [], drafts = {}) => {
  const values = {};
  limitDetails.forEach((limit) => {
    const draftValue = drafts[limit.key];
    const hasDraft = draftValue !== '' && draftValue !== null && draftValue !== undefined;
    values[limit.key] = hasDraft ? Number(draftValue) : Number(limit.effective_limit || 0);
  });
  return values;
};

const calculateEvaPeopleLimit = (values = {}) => {
  const seats = Number(values.employee_limit || 0);
  const tokensPerSeat = Number(values.tokens_per_seat_month || 0);
  if (seats === -1 || tokensPerSeat === -1) return -1;
  return Math.max(seats, 0) * Math.max(tokensPerSeat, 0);
};

const calculateTotalTokenPool = (values = {}) => {
  const evaPeopleTokens = calculateEvaPeopleLimit(values);
  const evaInsightsTokens = Number(values.kb_monthly_token_limit || 0);
  if (evaPeopleTokens === -1 || evaInsightsTokens === -1) return -1;
  return Math.max(evaPeopleTokens, 0) + Math.max(evaInsightsTokens, 0);
};

const getCalculatedLimitValue = (key, values = {}) => {
  if (key === 'conversation_monthly_token_limit') return calculateEvaPeopleLimit(values);
  if (key === 'monthly_token_limit') return calculateTotalTokenPool(values);
  return values[key];
};

const ENTERPRISE_HEURISTICS = {
  contentTeamRatio: 0.03,
  documentsPerEmployee: 3,
  topicsPerEmployee: 0.08,
  contentGapsPerEmployee: 1.5,
  integrationsPerEmployee: 0.01,
  exportsPerEmployee: 1,
  scheduledReportsPerEmployee: 0.02,
  notificationsPerEmployee: 0.07,
  tokensPerSeat: 75000,
  monthlyCostPerEmployee: 2,
};

const TRIAL_BASELINE = {
  durationDays: 10,
  employees: 5,
  document_limit: 25,
  max_chunks_per_document: 50,
  topics_limit: 15,
  content_gap_limit: 10,
  kb_monthly_token_limit: 250,
  employee_limit: 5,
  content_team_limit: 3,
  tokens_per_seat_month: 375,
  monthly_cost_limit_usd: 25,
  monthly_export_limit: 5,
  scheduled_reports_limit: 1,
  notification_limit: 3,
  integrations_limit: 2,
};

const getTrialDurationDays = (tenant) => {
  if (!tenant?.trial_started_at || !tenant?.trial_ends_at) return TRIAL_BASELINE.durationDays;
  const started = new Date(tenant.trial_started_at).getTime();
  const ends = new Date(tenant.trial_ends_at).getTime();
  if (!Number.isFinite(started) || !Number.isFinite(ends) || ends <= started) {
    return TRIAL_BASELINE.durationDays;
  }
  return Math.max(1, Math.round((ends - started) / (1000 * 60 * 60 * 24)));
};

const userCapacityGuardrailsForSeats = (seatCount) => {
  const seats = Math.max(Number(seatCount) || 1, 1);
  return {
    dailyCapacityPct: Number((70 / (30 * seats)).toFixed(4)),
    weeklyCapacityPct: Number(((70 * 7) / (30 * seats)).toFixed(4)),
    monthlyCapacityPct: Number((70 / seats).toFixed(4)),
  };
};

const PLAN_SELECT_ORDER = ['trial', 'starter', 'growth', 'scale', 'enterprise'];

const normalizePlanType = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '_');
const getPlanType = (plan) => normalizePlanType(plan?.type || plan?.plan_type || plan?.name);
const getPlanLabel = (plan) => plan?.display_name || plan?.name || formatCellValue(getPlanType(plan));
const getPlanSortIndex = (plan) => {
  const index = PLAN_SELECT_ORDER.indexOf(getPlanType(plan));
  return index === -1 ? PLAN_SELECT_ORDER.length : index;
};
const getUniquePlanOptions = (plans) => {
  const byType = new Map();
  plans
    .filter((plan) => getPlanType(plan))
    .sort((a, b) => getPlanSortIndex(a) - getPlanSortIndex(b))
    .forEach((plan) => {
      const type = getPlanType(plan);
      if (!byType.has(type)) byType.set(type, plan);
    });
  return Array.from(byType.values());
};

const buildPlanOptions = (pricingTiers = []) => {
  const activeTiers = pricingTiers
    .filter((tier) => tier?.is_active !== false && tier?.is_active !== 0)
    .map((tier) => ({
      ...tier,
      type: normalizePlanType(tier.name),
      display_name: tier.name,
    }));

  return getUniquePlanOptions([
    { type: 'trial', display_name: 'Trial' },
    ...activeTiers,
    { type: 'enterprise', display_name: 'Enterprise' },
  ]);
};

const normalizeTenantTab = (tab) => {
  if (tab === 'threads' || tab === 'sessions') return 'access';
  if (tab === 'subscription') return 'plan-limits';
  if (tab === 'knowledge-base') return 'configuration';
  if (tab === 'token-usage' || tab === 'tokens') return 'token-usage';
  return tab || 'overview';
};

const formatTokens = (tokens) => {
  const value = Number(tokens || 0);
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toLocaleString();
};

const formatCost = (value) => `$${Number(value || 0).toFixed(2)}`;
const describeRevenueSource = (financials) => {
  if (financials?.revenue_source === 'latest_paid_invoice') {
    return financials?.billing_period === 'annual'
      ? 'Actual recurring revenue (annual invoice ÷ 12)'
      : 'Actual recurring revenue (latest paid invoice)';
  }
  if (financials?.revenue_source === 'inactive_subscription') {
    return 'No active subscription';
  }
  return 'Estimated from current seats';
};
const calculateAverage = (total, count) => {
  const divisor = Number(count || 0);
  if (!divisor) return 0;
  return Number(total || 0) / divisor;
};
const formatCostPerCall = (cost, requests) => {
  const average = calculateAverage(cost, requests);
  if (average >= 0.01) return formatCost(average);
  return `$${average.toFixed(4)}`;
};
const OPERATION_ICON_MAP = {
  chat: MessageSquare,
  metadata_generation: Tags,
  document_analysis: FileText,
  session_summarization: CheckCircle,
  content_generation: FileText,
  research_document: BookOpen,
  pdf_agent: FileQuestion,
};
const MODEL_ICON_MAP = {
  'Eva Smart': Sparkles,
  'Eva Fast': Zap,
  'Eva Deep': Crown,
  'Eva Embed': Tags,
  'Eva System': Settings,
};
const IconLabel = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-2">
    {Icon ? <Icon className="h-4 w-4 flex-shrink-0 text-muted-foreground" /> : null}
    <span>{children}</span>
  </span>
);
const ProviderIcon = ({ provider }) => {
  const normalized = String(provider || '').trim().toLowerCase();
  if (normalized !== 'anthropic') return null;

  return (
    <span
      className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-[3px] border border-border bg-muted text-[9px] font-bold leading-none text-muted-foreground"
      title="Anthropic"
      aria-label="Anthropic"
    >
      A
    </span>
  );
};
const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : 'N/A');
const titleizeIdentifier = (value) => String(value || '')
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/\b\w/g, (char) => char.toUpperCase());

const formatBillingLabel = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  return titleizeIdentifier(value);
};

const formatPackageLabel = (value) => {
  if (!value) return 'N/A';
  const normalized = String(value).replace(/[_-]+/g, ' ').trim();
  return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatPercentValue = (value) => (
  value === null || value === undefined || Number.isNaN(Number(value))
    ? 'N/A'
    : `${Number(value).toFixed(1)}%`
);

const isIsoLikeDate = (value) => (
  typeof value === 'string'
  && /^\d{4}-\d{2}-\d{2}T/.test(value)
  && !Number.isNaN(new Date(value).getTime())
);

const formatBillingEventValue = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';
  if (isIsoLikeDate(value)) return formatDateTime(value);
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'None';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const formatEmployeeCount = (value) => {
  if (!value) return 'N/A';
  const count = Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(count)) return value;
  return count.toLocaleString();
};

const linksToText = (links = []) => (Array.isArray(links)
  ? links.map((link) => link?.url || link?.href || '').filter(Boolean).join('\n')
  : '');
const normalizeLinkUrl = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);
const textToLinks = (value = '') => value
  .split(/\n|,/)
  .map((url) => url.trim())
  .filter(Boolean)
  .map((url) => {
    const normalizedUrl = normalizeLinkUrl(url);
    return {
      name: normalizedUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0],
      url: normalizedUrl,
    };
  });

const paymentStatusClass = (status = '') => {
  const normalized = status.toLowerCase();
  if (['paid', 'completed', 'succeeded'].includes(normalized)) return 'bg-success/10 text-success border-success/30';
  if (['pending', 'processing'].includes(normalized)) return 'bg-warning/10 text-warning border-warning/30';
  if (['failed', 'expired', 'cancelled', 'canceled'].includes(normalized)) return 'bg-destructive/10 text-destructive border-destructive/30';
  return 'bg-muted text-muted-foreground border-border';
};

const SESSION_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const formatSessionDateTime = (value) => (value ? new Date(value).toLocaleString() : '—');

const getSessionDevice = (session) => (
  session.device_info || session.device || session.user_agent || 'Unknown device'
);

const getSessionUser = (session) => (
  session.email || session.user_email || session.user_name || 'Unknown user'
);

const getSessionStatus = (session) => {
  if (session.expires_at && new Date(session.expires_at).getTime() <= Date.now()) return 'Expired';
  return 'Active';
};

const withCurrentOption = (options, currentValue) => {
  if (!currentValue || options.includes(currentValue)) return options;
  return [currentValue, ...options];
};

export default function TenantEditorPage() {
  const { tenantId } = useParams();
  const { isSuperadmin, tenant: activeTenant, refreshTenant } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tenant, setTenant] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [config, setConfig] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [tokenUsage, setTokenUsage] = useState(null);
  const [tenantLimits, setTenantLimits] = useState(null);
  const [tenantBilling, setTenantBilling] = useState(null);
  const [tenantProfile, setTenantProfile] = useState(null);
  const [profileDraft, setProfileDraft] = useState({});
  const [limitDraft, setLimitDraft] = useState({});
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(normalizeTenantTab(requestedTab));
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [revokingSession, setRevokingSession] = useState(null);
  const [savingLimits, setSavingLimits] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [enterpriseSeats, setEnterpriseSeats] = useState('');
  const [trialRecalcEmployees, setTrialRecalcEmployees] = useState('');
  const [trialRecalcDays, setTrialRecalcDays] = useState('');
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [recordPaymentSaving, setRecordPaymentSaving] = useState(false);
  const [recordPaymentForm, setRecordPaymentForm] = useState({
    tier: '',
    user_count: '1',
    amount: '',
    billing_period: 'monthly',
    payment_method: 'bank_transfer',
    reference: '',
    notes: '',
  });
  const [limitsViewMode, setLimitsViewMode] = useState('list');
  const [sessionSearch, setSessionSearch] = useState('');
  const [sessionDeviceFilter, setSessionDeviceFilter] = useState('all');
  const [sessionSortKey, setSessionSortKey] = useState('last_activity');
  const [sessionSortDirection, setSessionSortDirection] = useState('desc');
  const [sessionPage, setSessionPage] = useState(1);
  const [sessionPageSize, setSessionPageSize] = useState(10);

  const loadAll = useCallback(async () => {
    try {
      const [tenantData, analyticsData, configData, sessionData, tokenData, plansData, limitsData, billingData, profileData] = await Promise.allSettled([
        superadminAPI.getTenant(tenantId),
        superadminAPI.getTenantAnalytics(tenantId).catch(() => null),
        superadminAPI.getTenantConfig(tenantId),
        superadminAPI.getTenantSessions(tenantId).catch(() => ({ sessions: [] })),
        superadminAPI.getTenantTokenUsage(tenantId).catch(() => null),
        superadminAPI.getPricingTiers().catch(() => ({ tiers: [] })),
        superadminAPI.getTenantLimits(tenantId).catch(() => null),
        superadminAPI.getTenantBilling(tenantId).catch(() => null),
        superadminAPI.getTenantProfile(tenantId).catch(() => null),
      ]);
      if (tenantData.status === 'fulfilled') setTenant(tenantData.value);
      if (analyticsData.status === 'fulfilled') setAnalytics(analyticsData.value);
      if (configData.status === 'fulfilled') {
        const cfg = configData.value;
        // Provide default topic classification config when none is stored
        if (!cfg.topic_classification_config) {
          cfg.topic_classification_config = { max_topics: 3, max_sub_topics_per_topic: 2, allow_emerging_topics: true };
        }
        setConfig(cfg);
      }
      if (sessionData.status === 'fulfilled') setSessions(sessionData.value?.sessions || []);
      if (tokenData.status === 'fulfilled') setTokenUsage(tokenData.value);
      if (plansData.status === 'fulfilled') setAvailablePlans(buildPlanOptions(plansData.value?.tiers || []));
      if (billingData.status === 'fulfilled') setTenantBilling(billingData.value);
      if (profileData.status === 'fulfilled' && profileData.value) {
        setTenantProfile(profileData.value);
        setProfileDraft({
          display_name: profileData.value.display_name || '',
          email_domains: profileData.value.email_domains || '',
          primary_color: profileData.value.primary_color || '#6366f1',
          secondary_color: profileData.value.secondary_color || '#818cf8',
          company_description: profileData.value.company_description || '',
          industry: profileData.value.industry || '',
          year_founded: profileData.value.year_founded || '',
          company_type: profileData.value.company_type || '',
          employee_count: profileData.value.employee_count || '',
          company_links_text: linksToText(profileData.value.company_links || []),
        });
      }
      if (limitsData.status === 'fulfilled' && limitsData.value) {
        setTenantLimits(limitsData.value);
        const draft = {};
        (limitsData.value.limit_details || []).forEach((limit) => {
          draft[limit.key] = limit.override_value ?? '';
        });
        setLimitDraft(draft);
        const employeeLimit = (limitsData.value.limit_details || []).find((limit) => limit.key === 'employee_limit');
        setEnterpriseSeats(String(employeeLimit?.override_value || employeeLimit?.effective_limit || employeeLimit?.current_usage || ''));
        setTrialRecalcEmployees(String(employeeLimit?.override_value || employeeLimit?.effective_limit || TRIAL_BASELINE.employees));
        const loadedTenant = tenantData.status === 'fulfilled' ? tenantData.value : null;
        setTrialRecalcDays(String(getTrialDurationDays(loadedTenant)));
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (!isSuperadmin) { navigate('/overview'); return; }
    loadAll();
  }, [tenantId, isSuperadmin, navigate, loadAll]);

  useEffect(() => {
    setActiveTab(normalizeTenantTab(searchParams.get('tab')));
  }, [searchParams]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await superadminAPI.updateTenantConfig(tenantId, {
        fast_model: config.fast_model,
        thinking_model: config.thinking_model,
        default_session_timeout_minutes: config.default_session_timeout_minutes,
        session_timeout_options: config.session_timeout_options,
        topic_classification_config: config.topic_classification_config,
        allowed_file_types: config.allowed_file_types,
        show_welcome_interstitial: config.show_welcome_interstitial,
        smart_eva_pulse_enabled: config.smart_eva_pulse_enabled,
      });
      toast.success('Configuration saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleExtendTrial = async (days) => {
    try {
      await superadminAPI.updateTenantTrial(tenantId, { extend_days: days });
      toast.success(`Trial extended by ${days} days`);
      loadAll();
      // EIP-169: loadAll only refreshes this page's local tenant state. When a
      // superadmin is viewing this tenant, the DashboardLayout trial banner is
      // driven by useAuth().tenant — refresh it so it reflects the new end date.
      if (tenantId === activeTenant?.tenant_id) {
        await refreshTenant();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to extend trial');
    }
  };

  const handleConvertToPaid = async () => {
    try {
      await superadminAPI.convertTenantToPaid(tenantId, { method: 'already_paid' });
      toast.success('Tenant converted to paid');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to convert to paid');
    }
  };

  const openRecordPaymentDialog = () => {
    setRecordPaymentForm({
      tier: tenant?.pricing_tier || '',
      user_count: '1',
      amount: '',
      billing_period: 'monthly',
      payment_method: 'bank_transfer',
      reference: '',
      notes: '',
    });
    setRecordPaymentOpen(true);
  };

  const handleRecordPayment = async () => {
    const userCount = Number(recordPaymentForm.user_count);
    const amount = Number(recordPaymentForm.amount);
    if (!recordPaymentForm.tier) {
      toast.error('Select a plan tier');
      return;
    }
    if (!Number.isFinite(userCount) || userCount <= 0) {
      toast.error('Enter a valid seat count');
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error('Enter a valid payment amount');
      return;
    }
    setRecordPaymentSaving(true);
    try {
      await superadminAPI.recordTenantPayment(tenantId, {
        tier: recordPaymentForm.tier,
        user_count: userCount,
        amount,
        billing_period: recordPaymentForm.billing_period,
        payment_method: recordPaymentForm.payment_method,
        reference: recordPaymentForm.reference || undefined,
        notes: recordPaymentForm.notes || undefined,
      });
      toast.success('Payment recorded — tenant is now active');
      setRecordPaymentOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record payment');
    } finally {
      setRecordPaymentSaving(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      if (tenant?.status === 'active') {
        await superadminAPI.deactivateTenant(tenantId);
        toast.success('Tenant deactivated');
      } else {
        await superadminAPI.activateTenant(tenantId);
        toast.success('Tenant activated');
      }
      setDeactivateOpen(false);
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update tenant status');
    }
  };

  const handleDelete = async () => {
    try {
      await superadminAPI.deleteTenant(tenantId);
      toast.success('Tenant deleted');
      navigate('/superadmin/tenants');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete tenant');
    }
  };

  const handleRevokeSession = async (sessionId) => {
    setRevokingSession(sessionId);
    try {
      await superadminAPI.revokeSession(tenantId, sessionId);
      toast.success('Session revoked');
      const data = await superadminAPI.getTenantSessions(tenantId).catch(() => ({ sessions: [] }));
      setSessions(data.sessions || []);
    } catch {
      toast.error('Failed to revoke session');
    } finally {
      setRevokingSession(null);
    }
  };

  const sessionDeviceOptions = useMemo(() => (
    Array.from(new Set(sessions.map((session) => getSessionDevice(session)).filter(Boolean))).sort()
  ), [sessions]);

  const filteredSessions = useMemo(() => {
    const query = sessionSearch.trim().toLowerCase();
    const filtered = sessions.filter((session) => {
      const userText = getSessionUser(session).toLowerCase();
      const deviceText = getSessionDevice(session).toLowerCase();
      const ipText = String(session.ip_address || '').toLowerCase();
      const userAgentText = String(session.user_agent || '').toLowerCase();
      const matchesSearch = !query
        || userText.includes(query)
        || deviceText.includes(query)
        || ipText.includes(query)
        || userAgentText.includes(query);
      const matchesDevice = sessionDeviceFilter === 'all' || getSessionDevice(session) === sessionDeviceFilter;
      return matchesSearch && matchesDevice;
    });

    const getValue = (session) => {
      if (sessionSortKey === 'user') return getSessionUser(session).toLowerCase();
      if (sessionSortKey === 'device') return getSessionDevice(session).toLowerCase();
      if (sessionSortKey === 'ip_address') return session.ip_address || '';
      if (sessionSortKey === 'status') return getSessionStatus(session);
      if (['created_at', 'last_activity', 'expires_at'].includes(sessionSortKey)) {
        return session[sessionSortKey] ? new Date(session[sessionSortKey]).getTime() : 0;
      }
      return session[sessionSortKey] || '';
    };

    return [...filtered].sort((a, b) => {
      const aValue = getValue(a);
      const bValue = getValue(b);
      if (aValue < bValue) return sessionSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sessionSortDirection === 'asc' ? 1 : -1;
      return String(a.id || '').localeCompare(String(b.id || ''));
    });
  }, [sessionDeviceFilter, sessionSearch, sessionSortDirection, sessionSortKey, sessions]);

  const sessionTotalPages = Math.max(Math.ceil(filteredSessions.length / sessionPageSize), 1);
  const sessionCurrentPage = Math.min(sessionPage, sessionTotalPages);
  const pagedSessions = filteredSessions.slice((sessionCurrentPage - 1) * sessionPageSize, sessionCurrentPage * sessionPageSize);

  const updateSessionSort = (key) => {
    if (sessionSortKey === key) {
      setSessionSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'));
    } else {
      setSessionSortKey(key);
      setSessionSortDirection(['created_at', 'last_activity', 'expires_at'].includes(key) ? 'desc' : 'asc');
    }
    setSessionPage(1);
  };

  const resetSessionTablePage = () => setSessionPage(1);

  const handleUpdatePlan = async (planType) => {
    try {
      await superadminAPI.updateTenantPlan(tenantId, planType);
      toast.success(planType === 'trial' ? 'Trial entitlement applied' : 'Entitlement assigned. Payment is required before activation.');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update plan');
    }
  };

  const handleResetUsage = async () => {
    try {
      await superadminAPI.resetTenantUsage(tenantId);
      toast.success('Usage reset');
      loadAll();
    } catch {
      toast.error('Failed to reset usage');
    }
  };

  const handleSaveLimits = async () => {
    const payload = {};
    (tenantLimits?.limit_details || []).forEach((limit) => {
      // EIP-229: the token-pool aggregates (conversation/monthly token limits)
      // stay derived — never persist them as overrides, or they pin the pool and
      // stop tracking seat / per-seat edits (the recurrence path for stray
      // token-pool overrides).
      if (CALCULATED_LIMIT_KEYS.has(limit.key)) return;
      const value = limitDraft[limit.key];
      if (value === '' || value === null || value === undefined) return;
      payload[limit.key] = value;
    });
    setSavingLimits(true);
    try {
      const updated = await superadminAPI.updateTenantLimits(tenantId, payload);
      setTenantLimits(updated);
      const draft = {};
      (updated.limit_details || []).forEach((limit) => {
        draft[limit.key] = limit.override_value ?? '';
      });
      setLimitDraft(draft);
      toast.success('Tenant limits updated');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update tenant limits');
    } finally {
      setSavingLimits(false);
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const payload = {
        display_name: profileDraft.display_name,
        email_domains: profileDraft.email_domains,
        primary_color: profileDraft.primary_color,
        secondary_color: profileDraft.secondary_color,
        company_description: profileDraft.company_description,
        industry: profileDraft.industry,
        year_founded: profileDraft.year_founded ? Number(profileDraft.year_founded) : null,
        company_type: profileDraft.company_type,
        employee_count: profileDraft.employee_count,
        company_links: textToLinks(profileDraft.company_links_text),
      };
      const response = await superadminAPI.updateTenantProfile(tenantId, payload);
      setTenantProfile(response.profile);
      setTenant((current) => ({
        ...current,
        display_name: response.profile?.display_name || current.display_name,
        email_domains: response.profile?.email_domains || current.email_domains,
        primary_color: response.profile?.primary_color || current.primary_color,
        secondary_color: response.profile?.secondary_color || current.secondary_color,
      }));
      setProfileDraft((draft) => ({
        ...draft,
        ...payload,
        year_founded: response.profile?.year_founded || payload.year_founded || '',
        company_links_text: linksToText(response.profile?.company_links || []),
      }));
      toast.success('Company profile saved');
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save company profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const formatLimitValue = (value, limit) => {
    if (value === null || value === undefined || value === '') return 'Not configured';
    if (limit?.unit === 'USD') return `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
    if (limit?.unit === '%') return `${Number(value).toLocaleString()}%`;
    return Number(value).toLocaleString();
  };

  const applyEnterpriseHeuristics = () => {
    const seats = Number(enterpriseSeats);
    if (!Number.isFinite(seats) || seats < 1) {
      toast.error('Enter an employee seat count first');
      return;
    }
    const contentTeam = Math.max(5, Math.ceil(seats * ENTERPRISE_HEURISTICS.contentTeamRatio));
    const documents = Math.max(250, Math.ceil(seats * ENTERPRISE_HEURISTICS.documentsPerEmployee));
    const topics = Math.max(25, Math.ceil(seats * ENTERPRISE_HEURISTICS.topicsPerEmployee));
    const contentGaps = Math.max(1000, Math.ceil(seats * ENTERPRISE_HEURISTICS.contentGapsPerEmployee));
    const integrations = Math.max(5, Math.ceil(seats * ENTERPRISE_HEURISTICS.integrationsPerEmployee));
    const monthlyExports = Math.max(25, Math.ceil(seats * ENTERPRISE_HEURISTICS.exportsPerEmployee));
    const scheduledReports = Math.max(3, Math.ceil(seats * ENTERPRISE_HEURISTICS.scheduledReportsPerEmployee));
    const notifications = Math.max(50, Math.ceil(seats * ENTERPRISE_HEURISTICS.notificationsPerEmployee));
    const tokenDisplayScale = Number(tenantLimits?.token_display_scale || 200) || 200;
    const evaPeopleTokens = Math.ceil((seats * ENTERPRISE_HEURISTICS.tokensPerSeat) / tokenDisplayScale);
    const kbMonthlyTokens = Math.ceil(Math.round(seats * ENTERPRISE_HEURISTICS.tokensPerSeat * 30 / 70) / tokenDisplayScale);
    const monthlyTokens = evaPeopleTokens + kbMonthlyTokens;
    const tokensPerSeat = Math.ceil(ENTERPRISE_HEURISTICS.tokensPerSeat / tokenDisplayScale);
    const monthlyCost = Math.max(100, Math.ceil(seats * ENTERPRISE_HEURISTICS.monthlyCostPerEmployee));
    const capacityGuardrails = userCapacityGuardrailsForSeats(seats);

    setLimitDraft((draft) => ({
      ...draft,
      employee_limit: String(seats),
      content_team_limit: String(contentTeam),
      document_limit: String(documents),
      topics_limit: String(topics),
      content_gap_limit: String(contentGaps),
      integrations_limit: String(integrations),
      monthly_export_limit: String(monthlyExports),
      scheduled_reports_limit: String(scheduledReports),
      notification_limit: String(notifications),
      monthly_token_limit: String(monthlyTokens),
      conversation_monthly_token_limit: String(evaPeopleTokens),
      kb_monthly_token_limit: String(kbMonthlyTokens),
      monthly_cost_limit_usd: String(monthlyCost),
      tokens_per_seat_month: String(tokensPerSeat),
      user_daily_capacity_pct: String(capacityGuardrails.dailyCapacityPct),
      user_weekly_capacity_pct: String(capacityGuardrails.weeklyCapacityPct),
      user_monthly_capacity_pct: String(capacityGuardrails.monthlyCapacityPct),
    }));
    toast.success('Enterprise limits drafted from seat count');
  };

  const applyTrialRecalculation = () => {
    const employees = Number(trialRecalcEmployees);
    const durationDays = Number(trialRecalcDays);
    if (!Number.isFinite(employees) || employees < 1) {
      toast.error('Enter an employee count first');
      return;
    }
    if (!Number.isFinite(durationDays) || durationDays < 1) {
      toast.error('Enter a trial duration first');
      return;
    }

    const peopleFactor = employees / TRIAL_BASELINE.employees;
    const durationFactor = durationDays / TRIAL_BASELINE.durationDays;
    const combinedFactor = peopleFactor * durationFactor;
    const scaleCount = (value, factor = combinedFactor, minimum = 1) => String(Math.max(minimum, Math.ceil(value * factor)));
    const tokensPerSeat = Math.max(1, Math.ceil(TRIAL_BASELINE.tokens_per_seat_month * durationFactor));
    const evaPeopleTokens = Math.max(0, Math.ceil(employees * tokensPerSeat));
    const evaInsightsTokens = Math.max(1, Math.ceil(TRIAL_BASELINE.kb_monthly_token_limit * combinedFactor));
    const totalTokens = evaPeopleTokens + evaInsightsTokens;
    // EIP-229: derive the per-user capacity guardrails from the seat count (as
    // applyEnterpriseHeuristics does), NOT the wrong-unit TRIAL_BASELINE 10/15/20
    // constants — otherwise the recalc drafts bad capacity overrides.
    const capacityGuardrails = userCapacityGuardrailsForSeats(employees);

    setLimitDraft((draft) => ({
      ...draft,
      employee_limit: String(Math.ceil(employees)),
      content_team_limit: scaleCount(TRIAL_BASELINE.content_team_limit, peopleFactor),
      document_limit: scaleCount(TRIAL_BASELINE.document_limit),
      max_chunks_per_document: String(TRIAL_BASELINE.max_chunks_per_document),
      topics_limit: scaleCount(TRIAL_BASELINE.topics_limit, peopleFactor),
      content_gap_limit: scaleCount(TRIAL_BASELINE.content_gap_limit),
      integrations_limit: scaleCount(TRIAL_BASELINE.integrations_limit, peopleFactor),
      monthly_export_limit: scaleCount(TRIAL_BASELINE.monthly_export_limit),
      scheduled_reports_limit: scaleCount(TRIAL_BASELINE.scheduled_reports_limit),
      notification_limit: scaleCount(TRIAL_BASELINE.notification_limit),
      user_daily_capacity_pct: String(capacityGuardrails.dailyCapacityPct),
      user_weekly_capacity_pct: String(capacityGuardrails.weeklyCapacityPct),
      user_monthly_capacity_pct: String(capacityGuardrails.monthlyCapacityPct),
      tokens_per_seat_month: String(tokensPerSeat),
      conversation_monthly_token_limit: String(evaPeopleTokens),
      kb_monthly_token_limit: String(evaInsightsTokens),
      monthly_token_limit: String(totalTokens),
      monthly_cost_limit_usd: String(Math.max(1, Math.ceil(TRIAL_BASELINE.monthly_cost_limit_usd * combinedFactor))),
    }));
    toast.success('Trial overrides recalculated. Save to apply them.');
  };

  const toggleFileType = (ext) => {
    const current = config?.allowed_file_types || ['pdf'];
    const updated = current.includes(ext)
      ? current.filter(t => t !== ext)
      : [...current, ext];
    if (updated.length === 0) { toast.error('At least one file type must be enabled'); return; }
    setConfig(c => ({ ...c, allowed_file_types: updated }));
  };

  const addSessionOption = (minutes) => {
    const opts = config?.session_timeout_options || [10, 30, 60];
    if (!opts.includes(minutes)) {
      setConfig(c => ({ ...c, session_timeout_options: [...opts, minutes].sort((a, b) => a - b) }));
    }
  };

  const removeSessionOption = (minutes) => {
    const opts = config?.session_timeout_options || [10, 30, 60];
    if (opts.length <= 1) { toast.error('At least one option required'); return; }
    const updated = opts.filter(o => o !== minutes);
    const def = config?.default_session_timeout_minutes;
    setConfig(c => ({
      ...c,
      session_timeout_options: updated,
      default_session_timeout_minutes: updated.includes(def) ? def : updated[0],
    }));
  };

  const limitSections = groupTenantLimitsBySection(tenantLimits?.limit_details || []);
  const limitValueMap = buildLimitValueMap(tenantLimits?.limit_details || [], limitDraft);

  if (loading) {
    return (
      <PageSkeleton
        maxWidth="7xl"
        kpis={4}
        tabs
        sections={2}
        sectionClassName="h-96"
        data-testid="tenant-editor-loading"
      />
    );
  }

  if (!tenant) {
    return (
      <PageShell maxWidth="7xl">
        <Button variant="ghost" onClick={() => navigate('/superadmin/tenants')}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <p className="text-center py-16 text-muted-foreground">Tenant not found</p>
      </PageShell>
    );
  }

  const statusColor = tenant.status === 'active' ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive';
  const tenantSubscriptionStatus = tenant.subscription_status || 'unknown';
  const subStatusColor = tenantSubscriptionStatus === 'paid' || tenantSubscriptionStatus === 'active'
    ? 'bg-success/10 text-success'
    : tenantSubscriptionStatus === 'trial' || tenantSubscriptionStatus === 'pending_payment'
      ? 'bg-warning/10 text-warning'
      : 'bg-muted text-muted-foreground';
  const showSubscriptionBadge = tenant.subscription_status && tenant.subscription_status !== tenant.status;
  const subscriptionStatus = tenantLimits?.subscription_status || tenant.subscription_status || 'unknown';
  const isTrialSubscription = TRIAL_SUBSCRIPTION_STATUSES.has(subscriptionStatus);
  const subscriptionActionStatus = tenant.subscription_status || subscriptionStatus;
  const subscriptionLabel = subscriptionActionStatus === 'trial_expired'
    ? 'Trial expired'
    : subscriptionActionStatus === 'active' || subscriptionActionStatus === 'paid'
      ? 'Paid'
      : formatBillingLabel(subscriptionActionStatus);
  const planLimitsLabel = isTrialSubscription
    ? 'Trial'
    : tenantLimits?.pricing_tier || tenant.pricing_tier || analytics?.billing_estimate?.tier_name || 'Plan';
  const overviewPlanLabel = isTrialSubscription
    ? 'Trial'
    : tenant.pricing_tier || tenantLimits?.pricing_tier || analytics?.billing_estimate?.tier_name || 'Not configured';
  const missingEnterpriseLimits = tenantLimits?.configuration?.missing_required_limits || [];
  const activationBlockedByLimits = tenant.status !== 'active' && missingEnterpriseLimits.length > 0;
  const hasRecordedPayment = Number(tenantBilling?.financials?.payment_count || 0) > 0;
  const activationBlockedByPayment = tenant.status !== 'active'
    && Boolean(tenant.pricing_tier)
    && !isTrialSubscription
    && tenantBilling
    && !hasRecordedPayment;
  const activationBlocked = activationBlockedByLimits || activationBlockedByPayment;
  // record-payment validates `tier` against an active dim_pricing_tiers row — Enterprise
  // isn't one (superadmin.py special-cases it), so only real paid tiers are offered here.
  const recordPaymentTierOptions = getUniquePlanOptions(availablePlans).filter(
    (plan) => getPlanType(plan) !== 'trial' && getPlanType(plan) !== 'enterprise'
  );
  const profile = tenantProfile || tenant;
  const brandPrimary = profile?.primary_color || tenant.primary_color || '#6366f1';
  const brandSecondary = profile?.secondary_color || tenant.secondary_color || '#818cf8';
  const brandLogo = profile?.logo_url_dark || profile?.logo_url || profile?.icon_url || tenant.logo_url;
  const companyLinks = profile?.company_links || [];

  return (
    <PageShell maxWidth="7xl" data-testid="tenant-editor-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground hover:bg-muted hover:text-foreground"
          onClick={() => navigate('/superadmin/tenants')}
          data-testid="back-to-tenants-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Tenants
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Building2 className="h-6 w-6 text-primary" />
              {profile?.display_name || tenant.display_name}
            </h1>
            <Badge className={statusColor}>{tenant.status}</Badge>
            {showSubscriptionBadge && <Badge className={subStatusColor}>{formatBillingLabel(tenant.subscription_status)}</Badge>}
            <span className="text-sm text-muted-foreground">/{tenant.shortname}</span>
          </div>
          {isTrialSubscription && tenant.trial_ends_at && (
            <p className="text-sm text-muted-foreground mt-0.5">
              Trial ends: {new Date(tenant.trial_ends_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value);
          if (value === 'overview') {
            setSearchParams({});
          } else {
            setSearchParams({ tab: value });
          }
        }}
      >
        <TabsList className="flex flex-wrap h-auto gap-1 bg-muted/50 p-1 rounded-lg">
          {[
            { value: 'overview', icon: BarChart3, label: 'Overview' },
            { value: 'plan-limits', icon: DollarSign, label: 'Plan & Limits' },
            { value: 'token-usage', icon: Coins, label: 'AI Usage' },
            { value: 'payments', icon: CreditCard, label: 'Payments' },
            { value: 'access', icon: Shield, label: 'Access & Conversations' },
            { value: 'configuration', icon: Settings, label: 'Configuration' },
            { value: 'danger', icon: AlertTriangle, label: 'Danger Zone' },
          ].map(tab => (
            <TabsTrigger key={tab.value} value={tab.value} className="flex items-center gap-1.5 text-sm" data-testid={`tab-${tab.value}`}>
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ===== OVERVIEW TAB ===== */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          {/* Summary cards */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[
              { label: 'Documents', value: analytics?.current_usage?.document_count ?? 0, icon: FileText, color: 'text-info' },
              { label: 'Employees', value: analytics?.current_usage?.employee_count ?? 0, icon: Users, color: 'text-success' },
              { label: 'Conversations (30d)', value: analytics?.current_usage?.conversation_count_30d ?? 0, icon: TrendingUp, color: 'text-primary' },
              { label: 'Pages', value: analytics?.current_usage?.chunk_count ?? 0, icon: Zap, color: 'text-warning' },
            ].map(stat => (
              <Card key={stat.label}>
                <CardContent className="p-4 flex items-center gap-3">
                  <stat.icon className={`h-8 w-8 ${stat.color}`} />
                  <div>
                    <p className="text-2xl font-bold">{stat.value}</p>
                    <p className="text-xs text-muted-foreground">{stat.label}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <DollarSign className="h-5 w-5 text-success" />
                Monthly P&amp;L
              </CardTitle>
              <CardDescription>
                Revenue and direct costs for the current monthly operating view.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
                {[
                  {
                    label: 'MRR',
                    value: formatCost(analytics?.financials?.mrr),
                    detail: describeRevenueSource(analytics?.financials),
                    icon: DollarSign,
                    color: 'text-success',
                  },
                  {
                    label: 'Token Cost',
                    value: formatCost(analytics?.financials?.token_cost),
                    detail: 'Current token period',
                    icon: Coins,
                    color: 'text-primary',
                  },
                  {
                    label: 'Cloud Cost',
                    value: formatCost(analytics?.financials?.cloud_cost),
                    detail: analytics?.financials?.cloud_cost_source === 'not_configured' ? 'Not configured' : 'Configured monthly cost',
                    icon: Cloud,
                    color: 'text-info',
                  },
                  {
                    label: 'Margin',
                    value: formatCost(analytics?.financials?.gross_margin),
                    detail: `${formatCost(analytics?.financials?.total_cost)} total cost`,
                    icon: TrendingUp,
                    color: Number(analytics?.financials?.gross_margin || 0) >= 0 ? 'text-success' : 'text-destructive',
                  },
                  {
                    label: 'Margin %',
                    value: formatPercentValue(analytics?.financials?.gross_margin_pct),
                    detail: 'Gross margin',
                    icon: Percent,
                    color: Number(analytics?.financials?.gross_margin_pct || 0) >= 0 ? 'text-success' : 'text-destructive',
                  },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-3 rounded-lg border bg-muted/20 p-4">
                    <item.icon className={`mt-0.5 h-5 w-5 ${item.color}`} />
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground">{item.label}</p>
                      <p className="mt-1 text-2xl font-bold">{item.value}</p>
                      <p className="mt-1 text-xs text-muted-foreground">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Tenant Details */}
          <Card className="overflow-hidden">
            <div
              className="h-2"
              style={{ background: `linear-gradient(90deg, ${brandPrimary}, ${brandSecondary})` }}
            />
            <CardHeader className="gap-4 md:flex-row md:items-start md:justify-between">
              <div className="flex min-w-0 items-start gap-4">
                <TenantIcon
                  name={profile?.display_name || tenant.display_name}
                  src={brandLogo}
                  color={brandPrimary}
                  className="h-16 w-16 overflow-hidden rounded-xl border"
                  textClassName="text-xl font-bold"
                  imgClassName="bg-background p-2"
                />
                <div className="min-w-0">
                  <CardTitle className="truncate text-xl">{profile?.display_name || tenant.display_name}</CardTitle>
                  <CardDescription className="mt-1 break-words">
                    {profile?.company_description || 'Company profile details and tenant identity.'}
                  </CardDescription>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Badge className={statusColor}>{tenant.status}</Badge>
                    <Badge variant="outline" className={LIMIT_SOURCE_STYLES[isTrialSubscription ? 'trial_override' : 'plan_default']}>
                      {overviewPlanLabel}
                    </Badge>
                    {profile?.industry && <Badge variant="outline">{profile.industry}</Badge>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="h-8 w-8 rounded-full border shadow-sm" style={{ backgroundColor: brandPrimary }} />
                <span className="h-8 w-8 rounded-full border shadow-sm" style={{ backgroundColor: brandSecondary }} />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <dl className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                {[
                  { label: 'Tenant ID', value: tenant.id },
                  { label: 'Short Name', value: profile?.shortname || tenant.shortname },
                  { label: 'Email Domains', value: profile?.email_domains || tenant.email_domains },
                  { label: 'Created', value: tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : 'N/A' },
                  { label: 'Plan', value: overviewPlanLabel },
                  { label: 'Est. Monthly', value: analytics?.billing_estimate?.monthly_total != null ? formatCost(analytics.billing_estimate.monthly_total) : 'N/A' },
                  { label: 'Founded', value: profile?.year_founded || 'N/A' },
                  { label: 'Employees', value: formatEmployeeCount(profile?.employee_count) },
                  { label: 'Company Type', value: profile?.company_type ? formatBillingLabel(profile.company_type) : 'N/A' },
                  { label: 'Updated', value: profile?.updated_at ? new Date(profile.updated_at).toLocaleDateString() : 'N/A' },
                ].map(item => (
                  <div key={item.label}>
                    <dt className="text-muted-foreground">{item.label}</dt>
                    <dd className="font-medium break-all">{item.value}</dd>
                  </div>
                ))}
              </dl>
              {companyLinks.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Company Links</p>
                  <div className="flex flex-wrap gap-2">
                    {companyLinks.map((link, index) => (
                      <Button key={`${link.url}-${index}`} variant="outline" size="sm" asChild>
                        <a href={link.url} target="_blank" rel="noreferrer">
                          <ExternalLink className="mr-2 h-4 w-4" />
                          {link.name || link.url}
                        </a>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== PLAN & LIMITS TAB ===== */}
        <TabsContent value="plan-limits" className="space-y-4 mt-4">
          <Card className="border-primary/30">
            <CardHeader className="space-y-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <CardTitle className="text-xl">{profile?.display_name || tenant.display_name}</CardTitle>
                    <Badge className={statusColor}>{tenant.status}</Badge>
                    <Badge variant="outline" className={LIMIT_SOURCE_STYLES[isTrialSubscription ? 'trial_override' : 'plan_default']}>
                      {subscriptionLabel}
                    </Badge>
                  </div>
                  <CardDescription className="mt-1">
                    {planLimitsLabel} limits with current usage and tenant overrides. Assigning entitlement prepares limits only; paid tenants remain inactive until payment is recorded.
                  </CardDescription>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <ViewModeToggle value={limitsViewMode} onChange={setLimitsViewMode} />
                  <div className="flex flex-nowrap items-center gap-2">
                    {getUniquePlanOptions(availablePlans).length > 0 && (
                      <Select onValueChange={handleUpdatePlan}>
                        <SelectTrigger className="w-48" data-testid="plan-select">
                          <SelectValue placeholder="Change Entitlement" />
                        </SelectTrigger>
                        <SelectContent>
                          {getUniquePlanOptions(availablePlans).map((plan) => {
                            const planType = getPlanType(plan);
                            const planName = getPlanLabel(plan);
                            return (
                              <SelectItem key={planType} value={planType}>
                                {planName}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    )}
                    <Button
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                      onClick={handleSaveLimits}
                      disabled={savingLimits || !tenantLimits}
                      data-testid="save-tenant-limits-btn"
                    >
                      {savingLimits ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {tenantLimits?.plan?.is_enterprise && (
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <div className="flex items-center gap-2 font-semibold">
                        <Crown className="h-4 w-4 text-primary" />
                        Enterprise sizing
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Enter contracted employee seats to draft numeric limits. You can adjust every value before saving.
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                      <div className="space-y-1">
                        <Label htmlFor="enterprise-seat-count">Employee seats</Label>
                        <Input
                          id="enterprise-seat-count"
                          className="w-full sm:w-40"
                          type="number"
                          min="1"
                          value={enterpriseSeats}
                          onChange={(e) => setEnterpriseSeats(e.target.value)}
                          data-testid="enterprise-seat-count"
                        />
                      </div>
                      <Button variant="outline" onClick={applyEnterpriseHeuristics} data-testid="apply-enterprise-heuristics-btn">
                        Populate Limits
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {isTrialSubscription && (
                <div className="rounded-lg border border-warning/25 bg-warning/5 p-4">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                      <div className="flex items-center gap-2 font-semibold">
                        <Clock className="h-4 w-4 text-warning" />
                        Recalculate trial overrides
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Scale the 10-day, 5-employee trial baseline by employee count and duration. Values are drafted until you save.
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                      <div className="space-y-1">
                        <Label htmlFor="trial-recalc-employees">Employees</Label>
                        <Input
                          id="trial-recalc-employees"
                          className="w-full sm:w-36"
                          type="number"
                          min="1"
                          value={trialRecalcEmployees}
                          onChange={(e) => setTrialRecalcEmployees(e.target.value)}
                          data-testid="trial-recalc-employees"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="trial-recalc-days">Duration days</Label>
                        <Input
                          id="trial-recalc-days"
                          className="w-full sm:w-36"
                          type="number"
                          min="1"
                          value={trialRecalcDays}
                          onChange={(e) => setTrialRecalcDays(e.target.value)}
                          data-testid="trial-recalc-days"
                        />
                      </div>
                      <Button variant="outline" onClick={applyTrialRecalculation} data-testid="apply-trial-recalculation-btn">
                        Recalculate Overrides
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {tenantLimits?.limit_details?.length ? (
                <>
                  {limitsViewMode === 'card' && (
                    <div className="space-y-6">
                      {limitSections.map((section) => (
                        <div key={section.key} className="space-y-3">
                          <h4 className="text-sm font-semibold text-muted-foreground">
                            <SectionLabel section={section} />
                          </h4>
                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                            {section.limits.map((limit) => {
                              const Icon = LIMIT_ICON_MAP[limit.key] || FileText;
                              const usage = limit.current_usage;
                              const hasUsage = usage !== null && usage !== undefined;
                              const percent = limit.effective_limit && hasUsage
                                ? Math.min((Number(usage) / Number(limit.effective_limit)) * 100, 100)
                                : 0;
	                              const draftValue = limitDraft[limit.key];
	                              const displayLimit = draftValue !== '' && draftValue !== null && draftValue !== undefined
	                                ? draftValue
	                                : limit.effective_limit;
	                              const isCalculated = CALCULATED_LIMIT_KEYS.has(limit.key);
	                              const calculatedLimit = isCalculated ? getCalculatedLimitValue(limit.key, limitValueMap) : displayLimit;
	                              return (
                                <div key={limit.key} className="rounded-lg border bg-card p-4 shadow-sm">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                        <Icon className="h-5 w-5" />
                                      </div>
                                      <div>
                                        <p className="font-semibold">{limit.label}</p>
                                        <p className="text-sm text-muted-foreground">{limit.unit}</p>
                                      </div>
                                    </div>
	                                    <Badge variant="outline" className={LIMIT_SOURCE_STYLES[limit.source] || LIMIT_SOURCE_STYLES.not_configured}>
	                                      {isCalculated ? 'Calculated' : limit.source_label}
	                                    </Badge>
                                  </div>
                                  <div className="mt-5 flex items-end justify-between gap-3">
                                    <div>
	                                      <p className="text-3xl font-bold">
	                                        {hasUsage ? formatLimitValue(usage, { ...limit, is_unlimited: false }) : formatLimitValue(calculatedLimit, limit)}
	                                      </p>
	                                      <p className="mt-1 text-sm text-muted-foreground">
	                                        {hasUsage
	                                          ? `${formatLimitValue(calculatedLimit, limit)} limit`
	                                          : limit.source === 'not_configured'
	                                            ? 'Limit required'
	                                            : isCalculated ? 'Derived from related limits' : 'Configured capacity'}
	                                      </p>
                                    </div>
                                    {limit.source === 'not_configured' && (
                                      <AlertTriangle className="h-5 w-5 text-destructive" />
                                    )}
                                  </div>
                                  {hasUsage && limit.effective_limit ? (
                                    <div className="mt-4 h-2 rounded-full bg-muted">
                                      <div
                                        className={`h-2 rounded-full ${percent >= 90 ? 'bg-destructive' : percent >= 75 ? 'bg-warning' : 'bg-success'}`}
                                        style={{ width: `${percent}%` }}
                                      />
                                    </div>
                                  ) : (
                                    <div className="mt-4 h-2 rounded-full bg-muted" />
                                  )}
	                                  <div className="mt-4">
	                                    <Label className="text-xs text-muted-foreground">{isCalculated ? 'Calculated value' : 'Override'}</Label>
	                                    {isCalculated ? (
	                                      <div className="mt-1 rounded-md border border-dashed bg-muted/40 px-3 py-2 font-medium">
	                                        {formatLimitValue(calculatedLimit, limit)}
	                                      </div>
	                                    ) : (
	                                      <Input
	                                        type="number"
	                                        min="0"
	                                        step={limit.type === 'decimal' ? '0.01' : '1'}
	                                        className="mt-1"
	                                        value={limitDraft[limit.key] ?? ''}
	                                        placeholder={limit.source === 'not_configured' ? 'Required' : 'Use plan default'}
	                                        onChange={(e) => setLimitDraft((draft) => ({ ...draft, [limit.key]: e.target.value }))}
	                                        data-testid={`limit-card-override-${limit.key}`}
	                                      />
	                                    )}
	                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <div className="rounded-lg border bg-muted/30 p-4">
                      <div className="flex items-center gap-2 font-semibold">
                        <Calendar className="h-4 w-4 text-primary" />
                        Access window
                      </div>
                      <p className="mt-2 text-2xl font-bold">
                        {tenantBilling
                          ? (tenantBilling.subscription?.ends_at
                              ? new Date(tenantBilling.subscription.ends_at).toLocaleDateString()
                              : 'No end date')
                          : (tenant.trial_ends_at ? new Date(tenant.trial_ends_at).toLocaleDateString() : 'No end date')}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {{
                          trial_ends: 'Trial end date',
                          next_billing: 'Next billing date',
                          renews: 'Renewal date',
                          access_until: 'Access end date',
                          ended: 'Ended on',
                          none_on_file: 'No date on file',
                        }[tenantBilling?.subscription?.display_date_kind]
                          || (tenant.subscription_status === 'trial' ? 'Trial end date' : 'Subscription access')}
                      </p>
                      {tenant.subscription_status === 'trial' && (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {[7, 14, 30].map((days) => (
                            <Button key={days} variant="outline" size="sm" onClick={() => handleExtendTrial(days)}>
                              +{days}d
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-4">
                      <div className="flex items-center gap-2 font-semibold">
                        <Coins className="h-4 w-4 text-primary" />
                        Period usage
                      </div>
                      <p className="mt-2 text-2xl font-bold">
                        {(tokenUsage?.current_period?.total_api_calls || 0).toLocaleString()}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        API calls this period
                      </p>
                      <Button className="mt-4" variant="outline" size="sm" onClick={handleResetUsage} data-testid="reset-usage-btn">
                        <RefreshCw className="h-4 w-4 mr-2" /> Reset Usage
                      </Button>
                    </div>
                    <div className="rounded-lg border bg-muted/30 p-4">
                      <div className="flex items-center gap-2 font-semibold">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        Subscription action
                      </div>
                      <p className="mt-2 text-2xl font-bold capitalize">
                        {subscriptionLabel}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Current billing status
                      </p>
                      {tenant.subscription_status === 'trial' && (
                        <Button className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90" size="sm" onClick={handleConvertToPaid} data-testid="convert-to-paid-btn">
                          Convert to Paid
                        </Button>
                      )}
                      {(tenant.status !== 'active' || !hasRecordedPayment) && (
                        <Button className="mt-4" variant="outline" size="sm" onClick={openRecordPaymentDialog} data-testid="record-payment-btn">
                          <Receipt className="h-4 w-4 mr-2" /> Record Payment
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div>
                      <p className="font-semibold">Unlimited entitlement</p>
                      <p className="text-sm text-muted-foreground">
                        Bypass all monthly resource limits for this tenant.
                      </p>
                    </div>
                    <Switch
                      checked={!!tenant.is_unlimited}
                      onCheckedChange={async (checked) => {
                        try {
                          await superadminAPI.updateTenantUnlimited(tenantId, checked);
                          setTenant((current) => ({ ...current, is_unlimited: checked ? 1 : 0 }));
                          toast.success(`Unlimited entitlement ${checked ? 'enabled' : 'disabled'}`);
                        } catch (e) {
                          toast.error(e.response?.data?.detail || 'Failed to update unlimited entitlement');
                        }
                      }}
                      data-testid="toggle-tenant-unlimited"
                    />
                  </div>

                  {limitsViewMode === 'list' && (
                    <div className="overflow-hidden rounded-lg border">
                      <div className="border-b bg-muted/40 p-4">
                        <p className="font-semibold">All limits and overrides</p>
                        <p className="text-sm text-muted-foreground">
                          Empty override fields use the plan default. Enterprise tenants require numeric overrides before activation.
                        </p>
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Section</TableHead>
                            <TableHead>Limit</TableHead>
                            <TableHead>Usage</TableHead>
                            <TableHead>Effective</TableHead>
                            <TableHead>Source</TableHead>
                            <TableHead className="w-[220px]">Override</TableHead>
                            <TableHead className="w-[150px] text-right">Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {limitSections.map((section) => (
	                            section.limits.map((limit, index) => {
	                              const isCalculated = CALCULATED_LIMIT_KEYS.has(limit.key);
	                              const calculatedLimit = isCalculated ? getCalculatedLimitValue(limit.key, limitValueMap) : limit.effective_limit;
	                              return (
	                                <TableRow key={limit.key}>
	                                  <TableCell className="text-muted-foreground">
	                                    {index === 0 ? <SectionLabel section={section} compact /> : ''}
	                                  </TableCell>
	                                  <TableCell>
	                                    <div className="font-medium">{limit.label}</div>
	                                    <div className="text-xs text-muted-foreground">{limit.unit}</div>
	                                  </TableCell>
	                                  <TableCell>{limit.current_usage === null || limit.current_usage === undefined ? '—' : formatLimitValue(limit.current_usage, limit)}</TableCell>
	                                  <TableCell className="font-medium">{formatLimitValue(calculatedLimit, limit)}</TableCell>
	                                  <TableCell>
	                                    <Badge variant="outline" className={isCalculated ? 'border-border bg-muted text-muted-foreground' : LIMIT_SOURCE_STYLES[limit.source] || LIMIT_SOURCE_STYLES.not_configured}>
	                                      {isCalculated ? 'Calculated' : limit.source_label}
	                                    </Badge>
	                                  </TableCell>
	                                  <TableCell>
	                                    {isCalculated ? (
	                                      <div className="rounded-md border border-dashed bg-muted/40 px-3 py-2 font-medium">
	                                        {formatLimitValue(calculatedLimit, limit)}
	                                      </div>
	                                    ) : (
	                                      <Input
	                                        type="number"
	                                        min="0"
	                                        step={limit.type === 'decimal' ? '0.01' : '1'}
	                                        value={limitDraft[limit.key] ?? ''}
	                                        placeholder=""
	                                        onChange={(e) => setLimitDraft((draft) => ({ ...draft, [limit.key]: e.target.value }))}
	                                        data-testid={`limit-override-${limit.key}`}
	                                      />
	                                    )}
	                                  </TableCell>
	                                  <TableCell className="text-right">
	                                    <Button
	                                      variant="secondary"
	                                      size="sm"
	                                      className="border border-border bg-background hover:bg-muted"
	                                      onClick={() => setLimitDraft((draft) => ({ ...draft, [limit.key]: '' }))}
	                                      disabled={isCalculated}
	                                    >
	                                      Use default
	                                    </Button>
	                                  </TableCell>
	                                </TableRow>
	                              );
	                            })
	                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </>
              ) : (
                <p className="py-8 text-center text-sm text-muted-foreground">No tenant limit data available.</p>
              )}
	              {tenantLimits?.plan?.is_enterprise && (
	                <div className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
	                  Enterprise tenants require numeric limits before activation. Use employee seats to populate suggested limits, then adjust as needed.
	                </div>
	              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== TOKEN USAGE TAB ===== */}
        <TabsContent value="token-usage" className="space-y-4 mt-4">
          {tokenUsage ? (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Shield className="h-4 w-4" />
                      Current Plan
                    </div>
                    <div className="mt-3">
                      <Badge className={LIMIT_SOURCE_STYLES[isTrialSubscription ? 'trial_override' : 'plan_default']}>
                        {isTrialSubscription ? subscriptionLabel : tokenUsage.plan?.display_name || overviewPlanLabel}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Zap className="h-4 w-4" />
                      Usage
                    </div>
                    <p className="mt-3 text-2xl font-bold">
                      {formatTokens(tokenUsage.current_period?.tokens_used)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <DollarSign className="h-4 w-4" />
                      Period Cost
                    </div>
                    <p className="mt-3 text-2xl font-bold">
                      {formatCost(tokenUsage.current_period?.cost_usd)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <BarChart3 className="h-4 w-4" />
                      API Calls
                    </div>
                    <p className="mt-3 text-2xl font-bold">
                      {(tokenUsage.current_period?.total_api_calls || 0).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Coins className="h-5 w-5 text-primary" />
                      AI Usage
                    </CardTitle>
                    <CardDescription>
                      Current period usage, limits, cost, and reset controls for {tenant.display_name}.
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
	                    {getUniquePlanOptions(availablePlans).length > 0 && (
	                      <Select onValueChange={handleUpdatePlan}>
	                        <SelectTrigger className="w-48" data-testid="token-usage-plan-select">
	                          <SelectValue placeholder="Change Entitlement" />
	                        </SelectTrigger>
                        <SelectContent>
                          {getUniquePlanOptions(availablePlans).map((plan) => {
                            const planType = getPlanType(plan);
                            return (
                              <SelectItem key={planType} value={planType}>
                                {getPlanLabel(plan)}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    )}
                    <Button variant="outline" onClick={handleResetUsage} data-testid="token-usage-reset-btn">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reset Usage
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <div className="rounded-lg border bg-muted/30 p-4">
                      <div className="mb-3 flex items-center justify-between gap-4">
                        <div>
                          <p className="font-semibold">Monthly Usage</p>
                          <p className="text-sm text-muted-foreground">
                            {formatTokens(tokenUsage.current_period?.tokens_used)} / {formatTokens(tokenUsage.plan?.token_limit)}
                          </p>
                        </div>
                        <span className={`text-lg font-bold ${
                          tokenUsage.current_period?.usage_percentage > 100 ? 'text-destructive' :
                          tokenUsage.current_period?.usage_percentage > 80 ? 'text-warning' :
                          'text-success'
                        }`}>
                          {(tokenUsage.current_period?.usage_percentage || 0).toFixed(1)}%
                        </span>
                      </div>
                      <Progress
                        value={Math.min(tokenUsage.current_period?.usage_percentage || 0, 100)}
                        className={`h-3 ${
                          tokenUsage.current_period?.usage_percentage > 100 ? '[&>div]:bg-destructive' :
                          tokenUsage.current_period?.usage_percentage > 80 ? '[&>div]:bg-warning' :
                          ''
                        }`}
                      />
                      {tokenUsage.current_period?.usage_percentage > 100 && (
                        <p className="mt-2 flex items-center gap-1 text-xs text-destructive">
                          <AlertTriangle className="h-3 w-3" />
                          Usage limit exceeded.
                        </p>
                      )}
                      {tokenUsage.current_period?.usage_percentage > 80 && tokenUsage.current_period?.usage_percentage <= 100 && (
                        <p className="mt-2 flex items-center gap-1 text-xs text-warning">
                          <AlertTriangle className="h-3 w-3" />
                          Tenant is approaching the monthly usage limit.
                        </p>
                      )}
                    </div>

                    <div className="rounded-lg border bg-muted/30 p-4">
                      <div className="mb-3 flex items-center justify-between gap-4">
                        <div>
                          <p className="font-semibold">Monthly Cost</p>
                          <p className="text-sm text-muted-foreground">
                            {formatCost(tokenUsage.current_period?.cost_usd)} used this period
                          </p>
                        </div>
                        <span className="text-lg font-bold">
                          {tokenUsage.plan?.cost_limit ? `$${Number(tokenUsage.plan.cost_limit).toLocaleString()}` : 'No cap'}
                        </span>
                      </div>
                      {tokenUsage.plan?.cost_limit ? (
                        <Progress
                          value={Math.min((Number(tokenUsage.current_period?.cost_usd || 0) / Number(tokenUsage.plan.cost_limit)) * 100, 100)}
                          className="h-3"
                        />
                      ) : (
                        <div className="h-3 rounded-full bg-muted" />
                      )}
                      <p className="mt-2 text-xs text-muted-foreground">
                        Period resets {tokenUsage.current_period?.period_end
                          ? new Date(tokenUsage.current_period.period_end).toLocaleDateString()
                          : 'monthly'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4">
                    <Card className="shadow-none">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <BarChart3 className="h-4 w-4" />
                          Usage by Operation
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {tokenUsage.breakdown?.by_operation?.length > 0 ? (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Operation</TableHead>
                                  <TableHead>Models</TableHead>
                                  <TableHead className="text-center">Requests</TableHead>
                                  <TableHead className="text-right">Tokens</TableHead>
                                  <TableHead className="text-right">Avg Tokens / Call</TableHead>
                                  <TableHead className="text-right">Avg Cost / Call</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {tokenUsage.breakdown.by_operation.map((op) => {
                                  const OperationIcon = OPERATION_ICON_MAP[op.operation];
                                  return (
                                    <TableRow key={op.operation}>
                                      <TableCell>
                                        <IconLabel icon={OperationIcon}>
                                          {formatCellValue(op.operation_label || op.operation)}
                                        </IconLabel>
                                      </TableCell>
                                      <TableCell>
                                        {op.models?.length > 0 ? (
                                          <div className="space-y-1">
                                            {op.models.map((model) => {
                                              const modelLabel = formatCellValue(model.model_label || model.model);
                                              const ModelIcon = MODEL_ICON_MAP[modelLabel];
                                              return (
                                                <div key={`${op.operation}-${model.provider || 'unknown'}-${model.model || 'unknown'}`} className="leading-tight">
                                                  <div className="text-sm font-medium text-foreground">
                                                    <IconLabel icon={ModelIcon}>{modelLabel}</IconLabel>
                                                  </div>
                                                  <div className="ml-6 flex max-w-[220px] items-center gap-1.5 text-xs text-muted-foreground" title={model.model || undefined}>
                                                    <ProviderIcon provider={model.provider} />
                                                    <span className="truncate">{formatCellValue(model.model)}</span>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <span className="text-sm text-muted-foreground">-</span>
                                        )}
                                      </TableCell>
                                      <TableCell className="text-center">{formatCellValue(op.requests)}</TableCell>
                                      <TableCell className="text-right">{formatTokens(op.tokens)}</TableCell>
                                      <TableCell className="text-right">{formatTokens(calculateAverage(op.tokens, op.requests))}</TableCell>
                                      <TableCell className="text-right">{formatCostPerCall(op.cost, op.requests)}</TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <p className="py-8 text-center text-sm text-muted-foreground">No operation usage yet.</p>
                        )}
                      </CardContent>
                    </Card>

                    <Card className="shadow-none">
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Zap className="h-4 w-4" />
                          Usage by Model
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {tokenUsage.breakdown?.by_model?.length > 0 ? (
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Model</TableHead>
                                  <TableHead className="text-center">Requests</TableHead>
                                  <TableHead className="text-right">Tokens</TableHead>
                                  <TableHead className="text-right">Cost</TableHead>
                                  <TableHead className="text-right">Avg Tokens / Call</TableHead>
                                  <TableHead className="text-right">Avg Cost / Call</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {tokenUsage.breakdown.by_model.map((model) => {
                                  const modelLabel = formatCellValue(model.model_label || model.model);
                                  const ModelIcon = MODEL_ICON_MAP[modelLabel];
                                  return (
                                    <TableRow key={model.model}>
                                      <TableCell>
                                        <div className="leading-tight">
                                          <div className="text-sm font-medium text-foreground">
                                            <IconLabel icon={ModelIcon}>{modelLabel}</IconLabel>
                                          </div>
                                          <div className="ml-6 flex max-w-[220px] items-center gap-1.5 text-xs text-muted-foreground" title={model.model || undefined}>
                                            <ProviderIcon provider={model.provider} />
                                            <span className="truncate">{formatCellValue(model.model)}</span>
                                          </div>
                                        </div>
                                      </TableCell>
                                      <TableCell className="text-center">{formatCellValue(model.requests)}</TableCell>
                                      <TableCell className="text-right">{formatTokens(model.tokens)}</TableCell>
                                      <TableCell className="text-right">{formatCost(model.cost)}</TableCell>
                                      <TableCell className="text-right">{formatTokens(calculateAverage(model.tokens, model.requests))}</TableCell>
                                      <TableCell className="text-right">{formatCostPerCall(model.cost, model.requests)}</TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <p className="py-8 text-center text-sm text-muted-foreground">No model usage yet.</p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Coins className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                <p className="font-medium">No AI usage data available</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  AI usage tracking will appear here after this tenant makes AI requests.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ===== PAYMENTS TAB ===== */}
        <TabsContent value="payments" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            {[
              {
                label: 'Subscription',
                value: formatBillingLabel(tenantBilling?.subscription?.display_status || tenantBilling?.subscription?.status || subscriptionStatus),
                helper: tenantBilling?.subscription?.is_valid
                  ? (tenantBilling.subscription.days_remaining != null
                      ? `${tenantBilling.subscription.days_remaining} days remaining`
                      : 'No expiration on file')
                  : 'Not currently valid',
                icon: Shield,
              },
              {
                label: 'Tier',
                value: tenantBilling?.subscription?.tier || overviewPlanLabel,
                helper: 'Current billing tier',
                icon: Crown,
              },
              {
                label: 'Total Revenue',
                value: formatCost(tenantBilling?.financials?.total_revenue || 0),
                helper: `${tenantBilling?.financials?.payment_count || 0} paid payments`,
                icon: DollarSign,
              },
              {
                label: 'Access Until',
                value: tenantBilling?.subscription?.ends_at
                  ? new Date(tenantBilling.subscription.ends_at).toLocaleDateString()
                  : 'No end date',
                helper: {
                  trial_ends: 'Trial end date',
                  next_billing: 'Next billing date',
                  renews: 'Renewal date',
                  access_until: 'Access end date',
                  ended: 'Ended on',
                  none_on_file: 'No date on file',
                }[tenantBilling?.subscription?.display_date_kind]
                  || (isTrialSubscription ? 'Trial access date' : 'Subscription access date'),
                icon: Calendar,
              },
            ].map((item) => (
              <Card key={item.label}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <item.icon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-muted-foreground">{item.label}</p>
                      <p className="mt-1 truncate text-2xl font-bold">{item.value}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{item.helper}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader className="gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-primary" />
                  Payment History
                </CardTitle>
                <CardDescription>
                  Payments recorded for this tenant. Entitlement changes only affect access; payment rows appear when checkout, invoice, or manual payment records are created.
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-muted text-muted-foreground">
                  {(tenantBilling?.payments || []).length} records
                </Badge>
                <Button variant="outline" size="sm" onClick={openRecordPaymentDialog} data-testid="record-payment-tab-btn">
                  <Receipt className="h-4 w-4 mr-2" /> Record Payment
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(tenantBilling?.payments || []).length ? (
                <div className="overflow-hidden rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Package</TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead className="text-right">Seats</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Invoice</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tenantBilling.payments.map((payment) => (
                        <TableRow key={payment.id}>
                          <TableCell className="whitespace-nowrap">{formatDateTime(payment.created_at)}</TableCell>
                          <TableCell className="font-medium">{formatPackageLabel(payment.package_id)}</TableCell>
                          <TableCell>{formatBillingLabel(payment.billing_period)}</TableCell>
                          <TableCell className="text-right">{payment.seats ?? <span className="text-muted-foreground">N/A</span>}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className={paymentStatusClass(payment.status)}>
                              {formatBillingLabel(payment.status || 'unknown')}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatCost(payment.amount)}
                          </TableCell>
                          <TableCell className="text-right">
                            {payment.stripe_invoice_url ? (
                              <Button variant="ghost" size="sm" asChild>
                                <a href={payment.stripe_invoice_url} target="_blank" rel="noreferrer">
                                  <ExternalLink className="mr-2 h-4 w-4" />
                                  Open
                                </a>
                              </Button>
                            ) : (
                              <span className="text-muted-foreground">N/A</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed py-12 text-center">
                  <CreditCard className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                  <p className="font-medium">No payments recorded</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    This tenant may have entitlement changes, but no payment transaction has been recorded yet.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Billing Events
              </CardTitle>
              <CardDescription>
                Audit trail for subscription, payment, invoice, and billing lifecycle changes.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(tenantBilling?.events || []).length ? (
                <div className="space-y-3">
                  {tenantBilling.events.map((event) => (
                    <div key={event.id} className="rounded-lg border p-4">
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                          <p className="font-semibold">{formatBillingLabel(event.type)}</p>
                          <p className="text-sm text-muted-foreground">{formatDateTime(event.created_at)}</p>
                        </div>
                        <Badge variant="outline" className="bg-muted text-muted-foreground">
                          Event
                        </Badge>
                      </div>
                      {event.data && Object.keys(event.data).length > 0 && (
                        <dl className="mt-3 grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                          {Object.entries(event.data).map(([key, value]) => (
                            <div key={key} className="rounded-md bg-muted/40 p-2">
                              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{formatBillingLabel(key)}</dt>
                              <dd className="mt-1 break-words font-medium">
                                {formatBillingEventValue(value)}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed py-10 text-center">
                  <Receipt className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                  <p className="font-medium">No billing events yet</p>
                  <p className="mt-1 text-sm text-muted-foreground">Subscription events will appear here as billing actions occur.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== CONFIGURATION TAB ===== */}
        <TabsContent value="configuration" className="space-y-4 mt-4">
          {config ? (
            <>
              <Card>
                <CardHeader className="gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-primary" />
                      Company Profile
                    </CardTitle>
                    <CardDescription>
                      Edit tenant identity, brand colors, and firmographic details shown throughout Eva.
                    </CardDescription>
                  </div>
                  <Button onClick={handleSaveProfile} disabled={savingProfile || !tenantProfile} data-testid="save-tenant-profile-btn">
                    {savingProfile ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                    Save Profile
                  </Button>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Company Name</Label>
                      <Input
                        value={profileDraft.display_name || ''}
                        onChange={(e) => setProfileDraft((draft) => ({ ...draft, display_name: e.target.value }))}
                        data-testid="tenant-profile-display-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email Domains</Label>
                      <Input
                        value={profileDraft.email_domains || ''}
                        onChange={(e) => setProfileDraft((draft) => ({ ...draft, email_domains: e.target.value }))}
                        data-testid="tenant-profile-email-domains"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Primary Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          className="h-10 w-14 p-1"
                          value={profileDraft.primary_color || '#6366f1'}
                          onChange={(e) => setProfileDraft((draft) => ({ ...draft, primary_color: e.target.value }))}
                          aria-label="Primary color"
                        />
                        <Input
                          value={profileDraft.primary_color || ''}
                          onChange={(e) => setProfileDraft((draft) => ({ ...draft, primary_color: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Secondary Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          className="h-10 w-14 p-1"
                          value={profileDraft.secondary_color || '#818cf8'}
                          onChange={(e) => setProfileDraft((draft) => ({ ...draft, secondary_color: e.target.value }))}
                          aria-label="Secondary color"
                        />
                        <Input
                          value={profileDraft.secondary_color || ''}
                          onChange={(e) => setProfileDraft((draft) => ({ ...draft, secondary_color: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      rows={3}
                      value={profileDraft.company_description || ''}
                      onChange={(e) => setProfileDraft((draft) => ({ ...draft, company_description: e.target.value }))}
                      data-testid="tenant-profile-description"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    <div className="space-y-2">
                      <Label>Industry</Label>
                      <Select
                        value={profileDraft.industry || ''}
                        onValueChange={(value) => setProfileDraft((draft) => ({ ...draft, industry: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select industry..." />
                        </SelectTrigger>
                        <SelectContent>
                          {withCurrentOption(BRANDFETCH_INDUSTRY_OPTIONS, profileDraft.industry).map((industry) => (
                            <SelectItem key={industry} value={industry}>{industry}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Founded</Label>
                      <Input
                        type="number"
                        value={profileDraft.year_founded || ''}
                        onChange={(e) => setProfileDraft((draft) => ({ ...draft, year_founded: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Company Type</Label>
                      <Select
                        value={profileDraft.company_type || ''}
                        onValueChange={(value) => setProfileDraft((draft) => ({ ...draft, company_type: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select company type..." />
                        </SelectTrigger>
                        <SelectContent>
                          {withCurrentOption(BRANDFETCH_COMPANY_KIND_OPTIONS, profileDraft.company_type).map((kind) => (
                            <SelectItem key={kind} value={kind}>{formatBillingLabel(kind)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Employees</Label>
                      <Input
                        value={profileDraft.employee_count || ''}
                        onChange={(e) => setProfileDraft((draft) => ({ ...draft, employee_count: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Company Links</Label>
                    <Textarea
                      rows={3}
                      placeholder="https://company.com&#10;https://linkedin.com/company/example"
                      value={profileDraft.company_links_text || ''}
                      onChange={(e) => setProfileDraft((draft) => ({ ...draft, company_links_text: e.target.value }))}
                    />
                    <p className="text-xs text-muted-foreground">Enter one URL per line.</p>
                  </div>
                </CardContent>
              </Card>

              {/* AI Models */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-purple-500" /> AI Models
                  </CardTitle>
                  <CardDescription>Set the Eva models used for standard and advanced responses</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Standard Model</Label>
                      <Select
                        value={config.fast_model}
                        onValueChange={v => setConfig(c => ({ ...c, fast_model: v }))}
                      >
                        <SelectTrigger data-testid="fast-model-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MODEL_OPTIONS.fast.map(m => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Advanced Model</Label>
                      <Select
                        value={config.thinking_model}
                        onValueChange={v => setConfig(c => ({ ...c, thinking_model: v }))}
                      >
                        <SelectTrigger data-testid="thinking-model-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MODEL_OPTIONS.thinking.map(m => (
                            <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Session Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-info" /> Session Behavior
                  </CardTitle>
                  <CardDescription>Configure session timeout options available to users</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Default Timeout</Label>
                    <Select
                      value={String(config.default_session_timeout_minutes || 10)}
                      onValueChange={v => setConfig(c => ({ ...c, default_session_timeout_minutes: parseInt(v) }))}
                    >
                      <SelectTrigger data-testid="default-timeout-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(config.session_timeout_options || [10, 30, 60]).map(opt => (
                          <SelectItem key={opt} value={String(opt)}>{opt} minutes</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Available Options</Label>
                    <div className="flex flex-wrap gap-2">
                      {(config.session_timeout_options || [10, 30, 60]).map(opt => (
                        <div key={opt} className="flex items-center gap-1 bg-muted px-3 py-1 rounded-full text-sm">
                          <span>{opt} min</span>
                          <button
                            onClick={() => removeSessionOption(opt)}
                            className="text-muted-foreground hover:text-destructive ml-1 text-xs"
                          >×</button>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      {[5, 10, 15, 30, 60, 120].filter(m => !(config.session_timeout_options || []).includes(m)).map(m => (
                        <Button key={m} variant="outline" size="sm" onClick={() => addSessionOption(m)}>+ {m}m</Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Topics Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Tags className="h-5 w-5 text-success" /> Topic Classification
                  </CardTitle>
                  <CardDescription>Configure how topics are extracted from conversations</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {config.topic_classification_config ? (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Max Topics</Label>
                          <Input
                            type="number" min={1} max={5}
                            value={config.topic_classification_config.max_topics ?? 3}
                            onChange={e => setConfig(c => ({
                              ...c,
                              topic_classification_config: { ...c.topic_classification_config, max_topics: parseInt(e.target.value) || 3 }
                            }))}
                            data-testid="max-topics-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Max Sub-Topics</Label>
                          <Input
                            type="number" min={1} max={3}
                            value={config.topic_classification_config.max_sub_topics_per_topic ?? 2}
                            onChange={e => setConfig(c => ({
                              ...c,
                              topic_classification_config: { ...c.topic_classification_config, max_sub_topics_per_topic: parseInt(e.target.value) || 2 }
                            }))}
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div>
                          <Label className="text-sm font-medium">Allow Emerging Topics</Label>
                          <p className="text-xs text-muted-foreground">Suggest new topics not in taxonomy</p>
                        </div>
                        <Switch
                          checked={config.topic_classification_config.allow_emerging_topics ?? true}
                          onCheckedChange={v => setConfig(c => ({
                            ...c,
                            topic_classification_config: { ...c.topic_classification_config, allow_emerging_topics: v }
                          }))}
                          data-testid="emerging-topics-switch"
                        />
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">Topic configuration not available for this tenant.</p>
                  )}
                </CardContent>
              </Card>

              {/* Knowledge Base Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileUp className="h-5 w-5 text-primary" /> Allowed File Types
                  </CardTitle>
                  <CardDescription>
                    Control which file types users can upload to the Knowledge Base. Only enabled types can be uploaded.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {FILE_TYPES.map(ft => {
                    const isEnabled = (config?.allowed_file_types || ['pdf']).includes(ft.ext);
                    return (
                      <div
                        key={ft.ext}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                          isEnabled ? 'bg-primary/5 border-primary/30' : 'bg-muted/30 border-border'
                        }`}
                        data-testid={`file-type-${ft.ext}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold ${isEnabled ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                            {ft.label}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{ft.label}</p>
                            <p className="text-xs text-muted-foreground">{ft.desc}</p>
                          </div>
                        </div>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={() => toggleFileType(ft.ext)}
                          data-testid={`toggle-${ft.ext}`}
                        />
                      </div>
                    );
                  })}
                </CardContent>
              </Card>

              {/* UX Features Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-purple-500" /> UX Features
                  </CardTitle>
                  <CardDescription>
                    Control user experience features for this tenant.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Show Welcome Interstitial</p>
                      <p className="text-xs text-muted-foreground">Display a welcome screen for new users on their first visit</p>
                    </div>
                    <Switch
                      checked={config?.show_welcome_interstitial ?? true}
                      onCheckedChange={v => setConfig(c => ({ ...c, show_welcome_interstitial: v }))}
                      data-testid="toggle-show-welcome-interstitial"
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Smart Eva Pulse</p>
                      <p className="text-xs text-muted-foreground">Show the conversational Eva Pulse assistant on Overview instead of the standard action list</p>
                    </div>
                    <Switch
                      checked={config?.smart_eva_pulse_enabled ?? false}
                      onCheckedChange={v => setConfig(c => ({ ...c, smart_eva_pulse_enabled: v }))}
                      data-testid="toggle-smart-eva-pulse"
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button onClick={saveConfig} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-config-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Save Configuration
                </Button>
              </div>
            </>
          ) : (
            <p className="text-center py-8 text-muted-foreground">Configuration not available</p>
          )}
        </TabsContent>

        {/* ===== ACCESS & CONVERSATIONS TAB ===== */}
        <TabsContent value="access" className="space-y-4 mt-4">
          <ChatThreadsPage
            tenantId={tenantId}
            tenantInfo={tenant}
            embedded
            activeSessionCount={sessions.length}
            afterKpis={(
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5 text-info" /> Active Sessions
                      <Badge variant="secondary">{sessions.length}</Badge>
                    </CardTitle>
                    {sessions.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          await superadminAPI.revokeAllSessions(tenantId);
                          toast.success('All sessions revoked');
                          loadAll();
                        }}
                        data-testid="revoke-all-sessions-btn"
                      >
                        Revoke All
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {sessions.length === 0 ? (
                    <div className="rounded-lg border border-dashed bg-muted/20 px-4 py-6 text-center">
                      <Shield className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
                      <p className="font-medium">No active sessions</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Users currently signed in to this tenant will appear here.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <DataTableToolbar
                        searchValue={sessionSearch}
                        onSearchChange={(value) => {
                          setSessionSearch(value);
                          resetSessionTablePage();
                        }}
                        searchPlaceholder="Search sessions..."
                        searchTestId="tenant-session-search"
                        className="border-0 bg-transparent p-0"
                      >
                        <Select
                          value={sessionDeviceFilter}
                          onValueChange={(value) => {
                            setSessionDeviceFilter(value);
                            resetSessionTablePage();
                          }}
                        >
                          <SelectTrigger className="w-full sm:w-48" data-testid="tenant-session-device-filter">
                            <SelectValue placeholder="Device" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All devices</SelectItem>
                            {sessionDeviceOptions.map((device) => (
                              <SelectItem key={device} value={device}>{device}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </DataTableToolbar>

                      <div className="overflow-x-auto rounded-lg border">
                        <Table className="min-w-[1100px]">
                          <TableHeader>
                            <TableRow>
                              <TableHead>
                                <DataTableSortHeader label="User" sortKey="user" activeSortKey={sessionSortKey} sortDirection={sessionSortDirection} onSort={updateSessionSort} align="left" />
                              </TableHead>
                              <TableHead>
                                <DataTableSortHeader label="Device" sortKey="device" activeSortKey={sessionSortKey} sortDirection={sessionSortDirection} onSort={updateSessionSort} align="left" />
                              </TableHead>
                              <TableHead>
                                <DataTableSortHeader label="IP Address" sortKey="ip_address" activeSortKey={sessionSortKey} sortDirection={sessionSortDirection} onSort={updateSessionSort} align="left" />
                              </TableHead>
                              <TableHead>
                                <DataTableSortHeader label="Created" sortKey="created_at" activeSortKey={sessionSortKey} sortDirection={sessionSortDirection} onSort={updateSessionSort} align="left" />
                              </TableHead>
                              <TableHead>
                                <DataTableSortHeader label="Last Active" sortKey="last_activity" activeSortKey={sessionSortKey} sortDirection={sessionSortDirection} onSort={updateSessionSort} align="left" />
                              </TableHead>
                              <TableHead>
                                <DataTableSortHeader label="Expires" sortKey="expires_at" activeSortKey={sessionSortKey} sortDirection={sessionSortDirection} onSort={updateSessionSort} align="left" />
                              </TableHead>
                              <TableHead className="text-center">
                                <DataTableSortHeader label="Status" sortKey="status" activeSortKey={sessionSortKey} sortDirection={sessionSortDirection} onSort={updateSessionSort} align="center" />
                              </TableHead>
                              <TableHead className="text-right">Action</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {pagedSessions.map(s => (
                              <TableRow key={s.id}>
                                <TableCell className="text-sm">
                                  <div className="flex items-center gap-3">
                                    <UserAvatar
                                      user={{
                                        name: s.user_name || getSessionUser(s),
                                        email: s.user_email || s.email,
                                        avatar_url: s.avatar_url,
                                      }}
                                      size="sm"
                                    />
                                    <div className="min-w-0">
                                      <p className="truncate font-medium">{formatCellValue(getSessionUser(s))}</p>
                                      {s.user_name && s.user_name !== getSessionUser(s) && (
                                        <p className="truncate text-xs text-muted-foreground">{s.user_name}</p>
                                      )}
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                  <div className="max-w-[220px]">
                                    <p className="font-medium text-foreground">{formatCellValue(getSessionDevice(s))}</p>
                                    {s.user_agent && (
                                      <p className="truncate text-xs text-muted-foreground" title={s.user_agent}>{s.user_agent}</p>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm text-muted-foreground">{formatCellValue(s.ip_address)}</TableCell>
                                <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{formatSessionDateTime(s.created_at)}</TableCell>
                                <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{formatSessionDateTime(s.last_activity)}</TableCell>
                                <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{formatSessionDateTime(s.expires_at)}</TableCell>
                                <TableCell className="text-center">
                                  <Badge variant="outline" className="bg-success/10 text-success border-success/30">
                                    {getSessionStatus(s)}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRevokeSession(s.id)}
                                    disabled={revokingSession === s.id}
                                    data-testid={`revoke-session-${s.id}-btn`}
                                  >
                                    {revokingSession === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4 text-destructive" />}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>

                      <DataTablePagination
                        page={sessionCurrentPage}
                        totalPages={sessionTotalPages}
                        totalItems={filteredSessions.length}
                        pageSize={sessionPageSize}
                        pageSizeOptions={SESSION_PAGE_SIZE_OPTIONS}
                        onPageChange={setSessionPage}
                        onPageSizeChange={(value) => {
                          setSessionPageSize(value);
                          setSessionPage(1);
                        }}
                        itemLabel="sessions"
                        pageSizeTestId="tenant-session-page-size"
                      />
                      <p className="text-xs text-muted-foreground">
                        Sessions are refresh-token records. Cookie access expires after the refresh token lifetime, and expired rows are removed from this active list before it is displayed.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          />
        </TabsContent>

        {/* ===== DANGER ZONE TAB ===== */}
        <TabsContent value="danger" className="space-y-4 mt-4">
          <Card className="border-destructive/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" /> Danger Zone
              </CardTitle>
              <CardDescription>
                Operational controls that can interrupt tenant access or permanently remove tenant data.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 lg:grid-cols-2">
                <div className="rounded-lg border p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-semibold">{tenant.status === 'active' ? 'Deactivate tenant' : 'Activate tenant'}</p>
	                      <p className="mt-1 text-sm text-muted-foreground">
	                        {tenant.status === 'active'
	                          ? 'Temporarily blocks users from accessing this tenant. Data is preserved.'
	                          : activationBlockedByLimits
	                            ? 'Configure Enterprise limits before restoring access for users assigned to this tenant.'
	                            : activationBlockedByPayment
	                              ? 'Record a paid subscription payment before restoring access for users assigned to this tenant.'
	                            : 'Restores access for users assigned to this tenant.'}
	                      </p>
                    </div>
                    <Button
                      variant="outline"
	                      className="shrink-0 border-destructive/50 text-destructive hover:bg-destructive/5"
	                      onClick={() => setDeactivateOpen(true)}
	                      disabled={activationBlocked}
	                      data-testid="toggle-status-btn"
	                    >
                      {tenant.status === 'active'
                        ? <><PowerOff className="h-4 w-4 mr-2" /> Deactivate</>
                        : <><Power className="h-4 w-4 mr-2" /> Activate</>}
	                    </Button>
	                  </div>
	                  {activationBlocked && (
	                    <p className="mt-3 text-sm text-destructive">
	                      {activationBlockedByLimits
	                        ? `Missing required limits: ${missingEnterpriseLimits.join(', ')}`
	                        : 'Payment required before tenant activation.'}
	                    </p>
	                  )}
	                </div>
                <div className="rounded-lg border border-destructive/30 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-semibold text-destructive">Delete tenant</p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Permanently deletes this tenant and associated data. This cannot be undone.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className="shrink-0 border-destructive text-destructive hover:bg-destructive/5"
                      onClick={() => setDeleteOpen(true)}
                      data-testid="delete-tenant-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Deactivate Dialog */}
      <AlertDialog open={deactivateOpen} onOpenChange={setDeactivateOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {tenant.status === 'active' ? 'Deactivate' : 'Activate'} {tenant.display_name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
	              {tenant.status === 'active'
	                ? 'Users will lose access to the platform. You can re-activate at any time.'
	                : activationBlockedByLimits
	                  ? 'Enterprise limits must be configured before users can regain access.'
	                  : activationBlockedByPayment
	                    ? 'A paid subscription payment must be recorded before users can regain access.'
	                  : 'Users will regain access to the platform.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
	            <AlertDialogAction onClick={handleDeactivate} disabled={activationBlocked} data-testid="confirm-deactivate-btn">
	              Confirm
	            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {tenant.display_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this tenant and all associated data. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
              data-testid="confirm-delete-btn"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Record Payment Dialog */}
      <Dialog open={recordPaymentOpen} onOpenChange={setRecordPaymentOpen}>
        <DialogContent data-testid="record-payment-dialog">
          <DialogHeader>
            <DialogTitle>Record a payment for {tenant.display_name}</DialogTitle>
            <DialogDescription>
              For offline payments (bank transfer, check, invoice already paid). This activates the
              tenant immediately and sets a real subscription end date.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="record-payment-tier">Plan *</Label>
                <Select
                  value={recordPaymentForm.tier}
                  onValueChange={(value) => setRecordPaymentForm({ ...recordPaymentForm, tier: value })}
                >
                  <SelectTrigger id="record-payment-tier" data-testid="record-payment-tier-select">
                    <SelectValue placeholder="Select a plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {recordPaymentTierOptions.map((plan) => (
                      <SelectItem key={plan.id || plan.display_name} value={plan.display_name}>
                        {getPlanLabel(plan)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="record-payment-billing-period">Billing period *</Label>
                <Select
                  value={recordPaymentForm.billing_period}
                  onValueChange={(value) => setRecordPaymentForm({ ...recordPaymentForm, billing_period: value })}
                >
                  <SelectTrigger id="record-payment-billing-period" data-testid="record-payment-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="record-payment-seats">Seats *</Label>
                <Input
                  id="record-payment-seats"
                  type="number"
                  min="1"
                  value={recordPaymentForm.user_count}
                  onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, user_count: e.target.value })}
                  data-testid="record-payment-seats-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="record-payment-amount">Amount (USD) *</Label>
                <Input
                  id="record-payment-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={recordPaymentForm.amount}
                  onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, amount: e.target.value })}
                  data-testid="record-payment-amount-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="record-payment-method">Payment method *</Label>
              <Select
                value={recordPaymentForm.payment_method}
                onValueChange={(value) => setRecordPaymentForm({ ...recordPaymentForm, payment_method: value })}
              >
                <SelectTrigger id="record-payment-method" data-testid="record-payment-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="check">Check</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="record-payment-reference">Reference (optional)</Label>
              <Input
                id="record-payment-reference"
                placeholder="Invoice or transfer reference number"
                value={recordPaymentForm.reference}
                onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, reference: e.target.value })}
                data-testid="record-payment-reference-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="record-payment-notes">Notes (optional)</Label>
              <Textarea
                id="record-payment-notes"
                value={recordPaymentForm.notes}
                onChange={(e) => setRecordPaymentForm({ ...recordPaymentForm, notes: e.target.value })}
                data-testid="record-payment-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecordPaymentOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRecordPayment} disabled={recordPaymentSaving} data-testid="confirm-record-payment-btn">
              {recordPaymentSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Receipt className="h-4 w-4 mr-2" />}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
